
import { defineComponent, onBeforeMount, reactive } from 'vue'

// components
import PageTitle from '@/common/components/UI/PageTitle/index.vue'
import FailedFetch from '@/common/components/UI/FailedFetch/index.vue'
import Modal from '@/common/components/UI/Modal/index.vue'
import TheSelect from '@/common/components/UI/TheSelect/index.vue'
import TheButton from '@/common/components/UI/TheButton/index.vue'
import Cheap from '@/common/components/UI/Cheap/index.vue'
import TheTable from '@/common/components/UI/TheTable/index.vue'
import TableScrollWrapper from '@/common/components/UI/TableScrollWrapper/index.vue'
import ButtonDropdown from '@/common/components/UI/ButtonDropdown/index.vue'
import NewLimit from '@/common/new-components/NewLimit/index.vue'
import NewPagination from '@/common/new-components/NewPagination/index.vue'

// module components
import InvoicesHistoryFilters from '@/modules/InvoicesHistoryModule/components/InvoicesHistoryFilters/index.vue'

// classes
import { DataState } from '@/core/classes/DataState.class'

// realisations
import { invoices } from '@/core/realisations/invoices.realisation'
import { companies } from '@/core/realisations/companies.realisation'
import { billing } from '@/core/realisations/billing.realisation'

// types
import {
    HistoryInvoiceT,
    HistoryInvoiceBillingT,
    InvoicesGetListParamsT,
} from '@/core/types/invoices.types'
import { CompanyT } from '@/core/types/companies.types'

// composable
import useInvoicesFilters from '@/modules/OrdersHistoryModule/composable/useInvoicesFilters'
import useNotifications from '@/common/composable/useNotifications'
import useAccount from '@/common/composable/useAccount'
import { useFile } from '@/common/composable/useFile'
import { useTablesStore } from '@/modules/TablesModule/store/useTablesStore'
import { formatDateWithTimeFromBackend, formatDateFromBackend } from '@/common/helpers/formatDate'

// constants
import { LTLInvoicesColumns, LtlStatusColors } from '@/core/constants/columns/LTL.columns'

export default defineComponent({
    components: {
        'page-title': PageTitle,
        'failed-fetch': FailedFetch,
        'invoices-history-filters': InvoicesHistoryFilters,
        modal: Modal,
        'the-select': TheSelect,
        'the-button': TheButton,
        Cheap,
        TheTable,
        TableScrollWrapper,
        ButtonDropdown,
        NewLimit,
        NewPagination,
    },
    setup() {
        const { havePermissions } = useAccount()
        const { saveFile } = useFile()
        const tablesStore = useTablesStore()

        const state = reactive({
            is_loading: false,
            is_quantity_loading: false,
            is_on_handle_billing: false,
            is_kaspi_report_loading: false,
            company_id: null as number | null,
            invoice_id: null as number | null,
            invoices: new DataState<HistoryInvoiceT>({}),
            limit: 50,
        })

        const data_state = reactive({
            companies: new DataState<CompanyT>({}),
        })

        const { filters_state, unsetFilters } = useInvoicesFilters()
        const notifications = useNotifications()

        const setTotalPages = () => {
            const totalPages = Math.ceil(state.invoices.pagination.total_items / state.limit)
            state.invoices.pagination.total_pages = totalPages
        }

        const getQuantity = async () => {
            try {
                state.is_quantity_loading = true

                const params_quantity = {
                    ...filters_state
                }

                const quantity = await invoices.getQuantity(params_quantity)
                state.invoices.pagination.total_items = quantity.data.quantity

                setTotalPages()
            } catch (error) {
                console.error(error)
            } finally {
                state.is_quantity_loading = false
            }
        }

        const loadInvoices = async (page: number) => {
            state.invoices.pagination.current_page = page

            try {
                state.invoices.is_loading = true

                const params: InvoicesGetListParamsT = {
                    page: state.invoices.pagination.current_page,
                    limit: state.limit,
                    ...filters_state,
                    isSelfDelivery: null,
                    isPickup: null,
                }

                if (filters_state.isSelfDelivery === '0') {
                    params.isSelfDelivery = 0
                } else if (filters_state.isSelfDelivery === '1') {
                    params.isSelfDelivery = 1
                }
                if (filters_state.isPickup === '0') {
                    params.isPickup = 0
                } else if (filters_state.isPickup === '1') {
                    params.isPickup = 1
                }

                const response = await invoices.getList(params)

                state.invoices.list = response.data
                setTotalPages()

                state.invoices.is_failed = false
                state.invoices.failure.error_state.is_cancel = false
            } catch (error) {
                state.invoices.failure.catchError(error)

                if (!state.invoices.failure.error_state.is_cancel) {
                    state.invoices.is_failed = true
                }
            } finally {
                if (!state.invoices.failure.error_state.is_cancel) {
                    state.invoices.is_loading = false
                }
            }
        }

        const applyFilters = () => {
            getQuantity()
            state.invoices.pagination.current_page = 1
            loadInvoices(state.invoices.pagination.current_page)
        }

        const resetFilters = () => {
            unsetFilters()
            getQuantity()
            state.invoices.pagination.current_page = 1
            loadInvoices(state.invoices.pagination.current_page)
        }

        const loadCompanies = async (page: number, search?: string) => {
            data_state.companies.pagination.current_page = page

            try {
                data_state.companies.is_loading = true
                const response = await companies.getList({ page, search, statusId: 2 })
                data_state.companies.list = response.data

                data_state.companies.pagination.total_pages = response.meta.last_page

                data_state.companies.is_failed = false
                data_state.companies.failure.error_state.is_cancel = false
            } catch (error) {
                data_state.companies.failure.catchError(error)

                if (!data_state.companies.failure.error_state.is_cancel) {
                    data_state.companies.is_failed = true
                }
            } finally {
                data_state.companies.is_loading = false
            }
        }

        const loadMoreCompanies = async (page: number) => {
            if (page > data_state.companies.pagination.total_pages) return
            data_state.companies.pagination.current_page = page

            try {
                data_state.companies.is_loading = true

                const response = await companies.getList({ page, statusId: 2 })
                data_state.companies.list = data_state.companies.list.concat(response.data)

                data_state.companies.pagination.total_pages = response.meta.last_page

                data_state.companies.is_failed = false
                data_state.companies.failure.error_state.is_cancel = false
            } catch (error) {
                data_state.companies.failure.catchError(error)

                if (!data_state.companies.failure.error_state.is_cancel) {
                    data_state.companies.is_failed = true
                }
            } finally {
                if (!data_state.companies.failure.error_state.is_cancel) {
                    data_state.companies.is_loading = false
                }
            }
        }

        const searchCompanies = (search_query: string) => {
            loadCompanies(1, search_query)
        }

        const unshiftFoundCompany = async (company: CompanyT) => {
            await loadCompanies(data_state.companies.pagination.current_page)
            data_state.companies.list.unshift(company)
        }

        const toggleBilling = async (invoice_id: number) => {
            if (!state.is_on_handle_billing) loadCompanies(1)

            state.is_on_handle_billing = !state.is_on_handle_billing
            state.invoice_id = invoice_id
        }

        const handleBilling = async () => {
            try {
                if (!state.invoice_id) return
                state.is_loading = true

                let params: HistoryInvoiceBillingT = { invoice_id: state.invoice_id }
                if (state.company_id) params.companyId = state.company_id

                await billing.handleBilling(params)

                notifications.notify({ title: 'Накладная прокинута в Биллинг', type: 'success' })
                state.is_on_handle_billing = false
            } catch (error) {
                notifications.notifyError({
                    error,
                    title: 'При прокидывании накладной произошла ошбика',
                })
            } finally {
                state.is_loading = false
            }
        }

        const cutName = (name: string, max_length: number): string => {
            if (!name) return '-'

            if (name && name.length > max_length) {
                let name_parts = name.split('')
                name_parts = name_parts.splice(0, max_length - 1)
                name_parts.push('...')
                return name_parts.join('')
            }

            return name
        }

        const getCheckOrTimesIcon = (is_check: number | boolean) => {
            return `fas ${is_check ? 'fa-check text-green-500' : 'fa-times'}`
        }

        const getShipmentIconClass = (shipment_name: string) => {
            return `fas ${shipment_name === 'Стандарт' ? 'fa-truck' : 'fa-plane'}`
        }

        const getCostPrediction = (row: HistoryInvoiceT) => {
            const { cost_prediction, status_code, payment_method } = row

            if (payment_method === 'Наличными') {
                return 'Наличными'
            } else if (cost_prediction === -1) {
                return 'Не указано'
            } else if (cost_prediction === null) {
                return 'Будет доступно после обработки груза'
            } else {
                if ([204, 212, 503].includes(status_code)) {
                    return ''
                } else if (status_code === 211) {
                    return cost_prediction + ' ₸'
                } else {
                    return 'от ' + cost_prediction + ' ₸'
                }
            }
        }

        const exportExcel = async () => {
            try {
                state.invoices.is_loading = true

                const response = await invoices.exportExcel({ ...filters_state, limit: 9999 })
                saveFile(response, 'Накладные')
            } catch (error) {
                notifications.notifyError({
                    error,
                    title: `Ошибка при скачивании`,
                })
            } finally {
                state.invoices.is_loading = false
            }
        }

        const downloadKaspiReport = async () => {
            try {
                state.is_kaspi_report_loading = true

                const response = await invoices.exportKaspiReport({ ...filters_state })
                saveFile(response, 'Отчет Каспи')
            } catch (error: any) {
                console.log(error)
                if (error.status === 400) {
                    notifications.notifyError({
                        error,
                        title: 'Максимальное количество накладных в отчете — 1500',
                    })
                } else {
                    notifications.notifyError({
                        error,
                        title: `Ошибка при скачивании`,
                    })
                }
            } finally {
                state.is_kaspi_report_loading = false
            }
        }

        const getCheapClass = (status_id: number) => {
            if (LtlStatusColors[status_id]) return LtlStatusColors[status_id]
            else if (status_id === 51) return 'red'
            else if (![1, 4].includes(status_id)) return 'yellow'

            return ''
        }

        const onLimitChange = (limit: number) => {
            state.limit = limit
            loadInvoices(1)
        }

        onBeforeMount(() => {
            getQuantity()
            loadInvoices(1)
        })

        return {
            havePermissions,
            state,
            data_state,
            loadCompanies,
            loadMoreCompanies,
            searchCompanies,
            unshiftFoundCompany,
            loadInvoices,
            applyFilters,
            resetFilters,
            toggleBilling,
            handleBilling,
            cutName,
            getCheckOrTimesIcon,
            getShipmentIconClass,
            getCostPrediction,
            exportExcel,
            downloadKaspiReport,
            onLimitChange,
            LTLInvoicesColumns,
            getCheapClass,
            tablesStore,
            formatDateWithTimeFromBackend,
            formatDateFromBackend,
        }
    },
})
